// CUSTOM: personalised-review-actions.js
// Review page actions and button management module.
// Handles all action-handling and button-management concerns for the personalised review page:
//   handleSubmitButton (edit update handler), initCheckoutButton, initAfterPayVisibility,
//   initDisclaimerVisibility, manageCheckoutButtonPosition, updateCheckoutButtonVisibility.
// Depends on: personalised-config.js, personalised-utils.js, personalised-recharge-utils.js,
//   personalised-bundle-state.js, personalised-bundle-ui.js, personalised-recharge-session.js (eventBus).

(function() {
  window.personalised = window.personalised || {};

  const reviewActions = (function() {

    // =============================================================================
    // ===== EDIT MODE ONLY — ACTION HANDLER =====
    // =============================================================================

    /**
     * [EDIT MODE ONLY] Handles the click event for the Update Subscription button.
     * Validates metadata, calls rechargeUtils.updateBundle(), resolves the Recharge portal URL,
     * and navigates to the portal on success.
     * @param {Event} event - The click event
     */
    async function handleSubmitButton(event) {
      const config = window.personalised.config;
      event.preventDefault();

      let navigating = false;

      // Verify we're in edit mode
      if (window.personalised.utils.getMode() !== config.modes.EDIT) {
        if (window.personalised?.debugMode) console.warn('Submit handler triggered outside edit mode');
        return;
      }

      // Get button reference
      const submitButton = event.currentTarget;

      // Add loading state and disable button
      if (submitButton) {
        submitButton.classList.add('loading');
        submitButton.disabled = true;
        // Show spinner for update flow
        if (window.personalised?.modalSpinner?.show) {
          window.personalised.modalSpinner.show('Loading your pack…');
        }
      }

      try {
        // Validate metadata integrity before update
        if (window.personalised.utils?.validateMetadataIntegrity) {
          const metadataValid = await window.personalised.utils.validateMetadataIntegrity();
          if (!metadataValid) {
            throw new Error('Missing critical metadata for bundle update');
          }
        }

        // Call update bundle function
        const result = await window.personalised.rechargeUtils.updateBundle();

        // Handle success
        if (result.success) {
          if (window.personalised?.debugMode) console.log('Bundle updated successfully');

          // Redirect to Recharge portal using portal-link component
          try {
            // Wait for the custom element to be defined
            customElements.whenDefined('portal-link').then(() => {
              const portalLink = document.querySelector('portal-link');
              let portalUrl = null;

              // Try multiple sources for portal URL (Shadow DOM-free fallback)
              if (portalLink && portalLink.shadowRoot) {
                const button = portalLink.shadowRoot.querySelector('a');
                if (button && button.href) {
                  portalUrl = button.href;
                }
              }

              // Fallback to data attribute
              if (!portalUrl && portalLink) {
                portalUrl = portalLink.getAttribute('data-portal-href');
              }

              // Fallback to hidden anchor
              if (!portalUrl) {
                const fallbackLink = document.getElementById('portal-link-fallback');
                if (fallbackLink && fallbackLink.href) {
                  portalUrl = fallbackLink.href;
                }
              }

              if (portalUrl) {
                if (window.personalised?.debugMode) console.log('Redirecting to Recharge portal:', portalUrl);
                try { window.personalised.storageManager.setSessionFresh(); } catch (e) {} // PR-4.3

                // Prevent spinner hide during navigation
                if (window.personalised?.modalSpinner?.setNavigating) {
                  window.personalised.modalSpinner.setNavigating(true);
                }
                navigating = true;

                // Always use web navigation for consistent same-WebView experience
                window.location.href = portalUrl;
              } else {
                console.error('Portal URL not found in any source (shadow, data-attr, fallback)');
                if (window.personalised?.debugMode) console.warn('Portal link URL resolution failed - no sources available');
              }
            });
          } catch (portalError) {
            console.error('Error redirecting to portal:', portalError);
          }
        } else {
          // Handle failure
          console.error('Failed to update bundle:', result.error);

          // Check for authentication errors
          if (window.personalised.utils?.handleAuthError) {
            window.personalised.utils.handleAuthError(result.error, 'handleSubmitButton');
          }

          // Optional alert for user feedback
          alert('There was an error updating your subscription. Please try again.');
        }
      } catch (err) {
        // Handle unexpected errors
        console.error('Error in bundle submit handler:', err);

        // Check for authentication errors
        if (window.personalised.utils?.handleAuthError) {
          window.personalised.utils.handleAuthError(err, 'handleSubmitButton');
        }
      } finally {
        // Only hide spinner and reset button if not navigating (error cases or navigation failures)
        // When navigating, let pagehide cleanup handle spinner removal
        if (!navigating) {
          if (window.personalised?.modalSpinner?.hide) {
            window.personalised.modalSpinner.hide();
          }
          // Reset button state
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.disabled = false;
          }
        }
      }
    }

    // =============================================================================
    // ===== SHARED — BUTTON MANAGEMENT =====
    // =============================================================================

    /**
     * [SHARED] Initialize checkout and update subscription buttons (desktop, mobile, and header).
     * Handles visibility and click-event binding based on current mode.
     */
    function initCheckoutButton() {
      const config = window.personalised.config;
      const wrapper = document.querySelector('#personalised-review-wrapper');

      if (!wrapper) {
        if (window.personalised?.debugMode) console.warn('Button initialization: Missing wrapper element');
        return;
      }

      // Get mode directly from data attribute or utils for safety
      let mode = wrapper.getAttribute('data-mode');
      if (!mode) {
        mode = window.personalised.utils.getMode();
        if (window.personalised.debugMode) console.log('Button initialization: No data-mode attribute found, using getMode():', mode);
      } else {
        if (window.personalised?.debugMode) console.log('Button initialization: Using data-mode attribute:', mode);
      }

      if (mode === 'edit') {
        // [EDIT ONLY] Force-hide checkout wrappers and ensure update-subscription is visible —
        // this provides a fast visual correction before the detailed visibility pass below.
        const desktopCheckoutWrap = document.getElementById('checkout-button-desktop-wrapper');
        const mobileCheckoutWrap  = document.getElementById('checkout-button-mobile-wrapper');
        const updateDesktopWrap   = document.getElementById('update-subscription-desktop-wrapper');
        const updateMobileWrap    = document.getElementById('update-subscription-mobile-wrapper');

        [desktopCheckoutWrap, mobileCheckoutWrap].forEach(el => {
          if (el) el.style.display = 'none';
        });
        // Ensure update subscription is visible
        [updateDesktopWrap, updateMobileWrap].forEach(el => {
          if (el) el.style.display = '';
        });

        // Optional: if checkout buttons were previously bound, ensure they remain inert in edit mode
        // (Do not remove listeners; just keep hidden)
      }

      const isEditMode = mode === config.modes.EDIT;
      const isGuestMode = mode === 'new' || mode === 'guest';
      if (window.personalised?.debugMode) console.log('Button visibility update:', isEditMode ? 'EDIT MODE' : 'GUEST/NEW MODE', 'Mode:', mode);

      // Update debug info
      if (wrapper) {
        wrapper.setAttribute('data-debug-mode', mode || 'unknown');
        if (window.personalised?.debugMode) console.debug('[Checkout Button Debug]', {
          mode: mode,
          viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile',
          desktopButtonVisible: document.getElementById('checkout-button-desktop-wrapper')?.style.display !== 'none',
          mobileButtonVisible: document.getElementById('checkout-button-mobile-wrapper')?.style.display !== 'none'
        });
      }

      // Get checkout button elements
      const checkoutDesktopWrapper = document.getElementById('checkout-button-desktop-wrapper');
      const checkoutMobileWrapper = document.getElementById('checkout-button-mobile-wrapper');
      const checkoutHeaderWrapper = document.getElementById('checkout-button-header-wrapper');
      const checkoutDesktopButton = document.getElementById('checkout-button-desktop');
      const checkoutMobileButton = document.getElementById('checkout-button-mobile');
      const checkoutHeaderButton = document.getElementById('checkout-button-header');

      // Get update subscription button elements
      const updateDesktopWrapper = document.getElementById('update-subscription-desktop-wrapper');
      const updateMobileWrapper = document.getElementById('update-subscription-mobile-wrapper');
      const updateHeaderWrapper = document.getElementById('update-subscription-header-wrapper');
      const updateDesktopButton = document.getElementById('update-subscription-desktop');
      const updateMobileButton = document.getElementById('update-subscription-mobile');
      const updateHeaderButton = document.getElementById('update-subscription-header');

      // Update checkout button visibility (show only in guest/new mode)
      if (checkoutDesktopWrapper) checkoutDesktopWrapper.style.display = isGuestMode ? '' : 'none';
      if (checkoutMobileWrapper) checkoutMobileWrapper.style.display = isGuestMode ? '' : 'none';
      if (checkoutHeaderWrapper) checkoutHeaderWrapper.classList.toggle('visible', isGuestMode);

      // Update subscription button visibility (show only in edit mode)
      if (updateDesktopWrapper) updateDesktopWrapper.style.display = isEditMode ? '' : 'none';
      if (updateMobileWrapper) updateMobileWrapper.style.display = isEditMode ? '' : 'none';
      if (updateHeaderWrapper) updateHeaderWrapper.classList.toggle('visible', isEditMode);

      // Add data attributes to wrapper for CSS targeting
      if (wrapper) {
        wrapper.setAttribute('data-checkout-visible', isGuestMode ? 'true' : 'false');
        wrapper.setAttribute('data-update-visible', isEditMode ? 'true' : 'false');
      }

      // [GUEST/NEW ONLY] Click handler for checkout buttons — cart redirect or direct form POST.
      const handleCheckoutClick = function(event) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        const directCheckoutEnabled = event.currentTarget.getAttribute('data-direct-checkout') === 'true';

        if (!directCheckoutEnabled) {
          // Fallback: current behavior
          if (window.personalised?.debugMode) console.log('Checkout button clicked, redirecting to /cart');
          if (window.personalised && window.personalised.eventBus) {
            window.personalised.eventBus.emit('checkoutRedirectClicked', {
              timestamp: new Date().toISOString(),
              mode: mode,
              viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile'
            });
          }
          window.location.href = window.personalised.config.routes.cart;
          return;
        }

        // Prevent double-submit
        if (event.currentTarget.disabled) return;
        event.currentTarget.disabled = true;

        if (window.personalised?.debugMode) console.log('Direct checkout initiated');

        // Analytics with direct checkout flag
        if (window.personalised && window.personalised.eventBus) {
          window.personalised.eventBus.emit('checkoutRedirectClicked', {
            timestamp: new Date().toISOString(),
            mode: mode,
            viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile',
            directCheckout: true
          });
        }

        // Create and submit form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = window.personalised.config.routes.cart;
        form.style.display = 'none';

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        form.appendChild(checkoutInput);

        document.body.appendChild(form);

        try {
          form.submit();
        } finally {
          setTimeout(() => {
            if (event.currentTarget) event.currentTarget.disabled = false;
            form.remove();
          }, 1000);
        }
      };

      // [GUEST/NEW ONLY] Bind checkout click handlers (only checkout buttons; update buttons are edit-only).
      [checkoutDesktopButton, checkoutMobileButton, checkoutHeaderButton].forEach(button => {
        if (button && !button.hasInitialized) {
          button.addEventListener('click', handleCheckoutClick);
          button.hasInitialized = true;
          if (window.personalised?.debugMode) console.log(`${button.id} event listener initialized`);
        }
      });

      // [EDIT ONLY] Bind update-subscription click handlers.
      [updateDesktopButton, updateMobileButton, updateHeaderButton].forEach(button => {
        if (button && !button.hasInitialized) {
          button.addEventListener('click', handleSubmitButton);
          button.hasInitialized = true;
          if (window.personalised?.debugMode) console.log(`${button.id} event listener initialized`);
        }
      });

    }

    /**
     * Manages checkout button positioning based on viewport size.
     * Places button inside the right column for desktop, outside columns for mobile.
     */
    function manageCheckoutButtonPosition() {
      const wrapper = document.querySelector('#personalised-review-wrapper');
      const rightColumn = document.querySelector('.personalised-review-right-wrapper');
      const checkoutContainer = document.getElementById('personalised-checkout-container');

      if (!wrapper || !rightColumn || !checkoutContainer) return;

      const isDesktop = window.matchMedia('(min-width: 1201px)').matches;

      if (isDesktop) {
        // For desktop: Move inside right column
        rightColumn.appendChild(checkoutContainer);
        checkoutContainer.classList.add('in-column');
      } else {
        // For mobile: Move outside columns (direct child of wrapper)
        wrapper.appendChild(checkoutContainer);
        checkoutContainer.classList.remove('in-column');
      }

      // After repositioning, check if we should display the button
      updateCheckoutButtonVisibility();
    }

    /**
     * Updates checkout and update subscription buttons visibility based on current mode.
     */
    function updateCheckoutButtonVisibility() {
      // Call initCheckoutButton which now handles both checkout and update subscription buttons
      initCheckoutButton();

      // Maintain container visibility logic for backward compatibility
      const wrapper = document.querySelector('#personalised-review-wrapper');
      const container = document.getElementById('personalised-checkout-container');

      if (!wrapper || !container) return;

      const mode = wrapper.getAttribute('data-mode') || window.personalised.utils.getMode();
      const isGuestMode = mode === 'new' || mode === 'guest';

      container.style.display = isGuestMode ? 'block' : 'none';
    }

    // =============================================================================
    // ===== SHARED — AFTERPAY AND DISCLAIMER =====
    // =============================================================================

    /**
     * [SHARED] Initialises Afterpay visibility based on the current bundle frequency.
     * Registers frequency-change radio listeners and responds to bundleStateLoaded events.
     * Idempotent — only registers listeners once per page load.
     */
    function initAfterPayVisibility() {
      if (initAfterPayVisibility._initialized) return;
      initAfterPayVisibility._initialized = true;

      const afterpayElement = document.querySelector('.personalised-review-afterpay');
      const afterpayText = document.querySelector('.personalised-review-afterpay-text');
      const afterpayRow = document.querySelector('.personalised-review-payment-option');
      if (!afterpayElement || !afterpayText) return;

      const shouldShowAfterpay = window.personalised?.config?.shouldShowAfterpay;
      if (typeof shouldShowAfterpay !== 'function' || !shouldShowAfterpay({ source: 'review_status_text' })) {
        afterpayElement.classList.add('disabled');
        afterpayText.textContent = '';
        if (afterpayRow) afterpayRow.style.display = 'none';
        return;
      }

      if (afterpayRow) afterpayRow.style.display = '';

      function updateAfterPayStatus(frequency) {
        if (frequency === '30') {
          afterpayElement.classList.add('disabled');
          afterpayText.textContent = 'only available for 2 or 3 monthly pack orders';
        } else {
          afterpayElement.classList.remove('disabled');
          afterpayText.textContent = 'available as payment option';
        }
      }

      // Listen for frequency changes via radio buttons
      document.querySelectorAll('input[name="bundle-frequency"]').forEach(radio => {
        radio.addEventListener('change', function() {
          updateAfterPayStatus(this.value);
        });
      });

      // Re-evaluate when bundle state is loaded or reloaded
      window.personalised.eventBus.on('bundleStateLoaded', function() {
        const frequency = window.personalised.bundle.getFrequency?.();
        if (frequency) updateAfterPayStatus(frequency);
      });

      // Immediate update using the current bundle frequency (state already loaded by this point)
      const frequency = window.personalised.bundle.getFrequency?.();
      if (frequency) updateAfterPayStatus(frequency);
    }

    /**
     * [GUEST/NEW MODE ONLY] Shows checkout disclaimers for non-edit modes.
     * Never called in edit mode — edit mode uses the update-subscription button, not checkout.
     * Called early in initReviewPage so disclaimers appear as soon as mode is known.
     * @param {string} mode - The current bundle mode.
     */
    function initDisclaimerVisibility(mode) {
      if (mode !== 'edit') {
        document.getElementById('checkout-disclaimer-desktop')?.style.setProperty('display', 'block');
        document.getElementById('checkout-disclaimer-mobile')?.style.setProperty('display', 'block');
      }
    }

    // Public API
    return {
      handleSubmitButton,
      initCheckoutButton,
      manageCheckoutButtonPosition,
      updateCheckoutButtonVisibility,
      initAfterPayVisibility,
      initDisclaimerVisibility
    };
  })();

  window.personalised.reviewActions = reviewActions;
})();
